import { useMemo } from "react";
import { useQuery, type QueryKey } from "react-query";

import type {
  ListStartedFeaturesResponse,
  StartedFeatureName,
} from "@/api/splashpages";
import type { Feature } from "@/types/feature";
import { getCurrentUser } from "@/auth/firebase";
import { makeRequest } from "@/util";

export type UseHasEngagedWithFeatureResult = {
  engaged: boolean;
  loading: boolean;
};

export type UseStartedFeaturesResult = {
  startedFeatures: Set<StartedFeatureName>;
  loading: boolean;
};

export function startedFeaturesQueryKey(userId: string | undefined): QueryKey {
  return ["list_started_features", { userId: userId ?? "__anonymous__" }];
}

// Reads the set of features the current user has clicked "Get Started" on,
// backed by the `started_features` table. Shared by every engagement consumer
// so they all read (and optimistically update) the same cached query.
export function useStartedFeatures(): UseStartedFeaturesResult {
  // Use the Firebase UID directly so this works even when masquerading,
  // where currentUser in the Redux store is undefined (the employee's UID
  // isn't in the customer's team list).
  const userId = getCurrentUser()?.uid;

  const { data, isLoading } = useQuery<ListStartedFeaturesResponse>(
    startedFeaturesQueryKey(userId),
    async () => {
      const res = await makeRequest<ListStartedFeaturesResponse>(
        "list_started_features",
        {},
      );
      return res.data;
    },
    {
      enabled: !!userId,
      staleTime: 60_000,
    },
  );

  const startedFeatures = useMemo(
    () => new Set((data?.features ?? []).map((f) => f.featureName)),
    [data],
  );

  return { startedFeatures, loading: isLoading };
}

export function useHasEngagedWithFeature(
  feature: Feature,
): UseHasEngagedWithFeatureResult {
  const userId = getCurrentUser()?.uid;
  const { startedFeatures, loading } = useStartedFeatures();

  // Pending userId is also loading; otherwise consumers would see a
  // "not engaged" flash before the query starts.
  return {
    engaged: startedFeatures.has(feature),
    loading: !userId || loading,
  };
}
